import { useState, useCallback, useEffect, CSSProperties } from "react"
import { Close, InfoOutlined } from "@mui/icons-material"
import { useTranslation } from "react-i18next" 
import useBannerSeenMutation from "../hooks/useBannerSeenMutation";

interface UserMessageBannerProps {
  message?: string;
  showBanner?: boolean;
  onClose?: () => void;
  tenantId?: string;
  hospitalId?: string;
}

const styles: Record<string, CSSProperties> = {
  banner: {
    backgroundColor: "#E8DEF8",
    padding: "12px 16px",
    position: "fixed",
    left: 64,
    top: 64,
    right: 0,
    zIndex: 1301,
    boxSizing: "border-box",
  },
  content: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    maxWidth: "100%",
  },
  left: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    flex: 1,
  },
  icon: {
    width: "24px",
    height: "24px",
    color: "#000000",
    flexShrink: 0,
  },
  title: {
    fontWeight: 700,
    fontSize: "16px",
    margin: "0 0 4px 0",
  },
  description: {
    fontSize: "13px",
    margin: 0,
  },
  closeButton: {
    background: "none",
    border: "none",
    padding: "8px",
    cursor: "pointer",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "32px",
    minHeight: "32px",
    flexShrink: 0,
    transition: "background-color 0.2s ease",
  },
  closeIcon: {
    width: "20px",
    height: "20px",
    color: "#000000",
  },
}

const ANIMATION_STYLES = `
  @keyframes slideDown {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  .slide-down {
    animation: slideDown 0.3s ease-out;
  }
`

/**
 * UserMessageBanner component displays a notification banner for newly added users
 * @param {string} message - The message to display in the banner
 * @param {boolean} showBanner - Whether the banner should be shown
 * @param {() => void} onClose - Optional callback function when banner is closed
 * @returns {JSX.Element | null} The banner component or null if not visible
 */
const UserMessageBanner = ({ message, showBanner, onClose, tenantId, hospitalId }: UserMessageBannerProps): JSX.Element | null => {
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const { t } = useTranslation();
  const { mutate: bannerSeen } = useBannerSeenMutation();

  const handleClose = useCallback(() => {
    setIsVisible(false)
    onClose?.()
  }, [onClose])

  useEffect(() => {
    if (isVisible && showBanner && message && tenantId && hospitalId) {
      bannerSeen({ tenantId, hospitalId });
    }
   
  }, [isVisible, showBanner, message, tenantId, hospitalId, bannerSeen]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      const styleSheet = document.createElement('style')
      styleSheet.textContent = ANIMATION_STYLES
      document.head.appendChild(styleSheet)

      return () => {
        document.head.removeChild(styleSheet)
      }
    }
  }, [])

  if (!isVisible || !showBanner || !message) return null;

  return (
    <div
      style={styles.banner}
      role="alert"
      aria-live="polite"
      className="slide-down"
    >
      <div style={styles.content}>
        <div style={styles.left}>
          <div>
            <InfoOutlined style={styles.icon} />
          </div>

          <div>
            <h3 style={styles.title}>
              {t("Banner_New_Users_Added")}
            </h3>
            <p style={styles.description}>
              {message}
            </p>
          </div>
        </div>

        <button 
          onClick={handleClose} 
          style={styles.closeButton}
          aria-label="Close banner"
          type="button"
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <Close style={styles.closeIcon} />
        </button>
      </div>
    </div>
  )
}

export default UserMessageBanner